import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import AudienceEditor, { DEFAULT_AUDIENCE } from "../components/AudienceEditor";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Lock, Users, Search, X, Target } from "lucide-react";

const CATS = [
  ["tema",   "Tema"],
  ["sted",   "Sted"],
  ["fetisj", "Fetisj"],
  ["annet",  "Annet"],
];

export default function Groups() {
  const { t } = useTranslation();
  const TABS = [
    { k: "explore", label: t("groups_page.tab_explore") },
    { k: "mine",    label: t("groups_page.tab_mine") },
    { k: "create",  label: t("groups_page.tab_create") },
  ];
  const { user } = useAuth();
  const [tab, setTab] = useState("explore");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Create form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("tema");
  const [tags, setTags] = useState("");
  const [rules, setRules] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [audience, setAudience] = useState({ ...DEFAULT_AUDIENCE });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isVip = !!user?.is_vip;

  useEffect(() => {
    if (tab === "create") return;
    setLoading(true);
    api.get(`/groups?tab=${tab}`).then((r) => setGroups(r.data)).finally(()=>setLoading(false));
  }, [tab]);

  const filtered = useMemo(() => {
    if (!q.trim()) return groups;
    const needle = q.trim().toLowerCase();
    return groups.filter((g) =>
      (g.name || "").toLowerCase().includes(needle) ||
      (g.description || "").toLowerCase().includes(needle) ||
      (g.tags || []).some((t) => (t || "").toLowerCase().includes(needle))
    );
  }, [q, groups]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (description.trim().length < 10) return setError("Beskrivelsen må være minst 10 tegn.");
    setSubmitting(true);
    try {
      const tag_arr = tags.split(",").map((t)=>t.trim()).filter(Boolean);
      const { data } = await api.post("/groups", {
        name, description, category, tags: tag_arr, rules, is_private: isPrivate,
        requires_approval: requiresApproval,
        audience,
      });
      window.location.href = `/grupper/${data.id}`;
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout withSidebar={false}>
      {/* Kompakt header: tittel venstre, søk + faner høyre */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-x-12 gap-y-6 mb-10 items-start">
        <div>
          <div className="font-meta mb-2">{t("groups_page.community").toUpperCase()}</div>
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight">
            {t("groups_page.title")}.
          </h1>
        </div>

        <div className="lg:pt-3 space-y-3">
          {/* Linje 1: søkefelt */}
          {tab !== "create" && (
            <div className="flex justify-end">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--cream-faint)] pointer-events-none" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("groups_page.search_placeholder")}
                  className="w-full bg-transparent border border-white/10 hover:border-white/20 focus:border-[var(--eucalyptus)] focus:outline-none px-7 py-1.5 text-[11px] uppercase tracking-[0.15em] font-manrope text-[var(--cream)] placeholder:text-[var(--cream-faint)]/60 transition-colors"
                  data-testid="groups-search"
                />
                {q && (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                    data-testid="groups-search-clear"
                    aria-label="Tøm søk"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Linje 2: faner */}
          <div className="flex gap-6 border-b border-white/10 lg:justify-end">
            {TABS.map((t) => (
              <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`groups-tab-${t.k}`}
                className={`pb-3 font-manrope text-[11px] uppercase tracking-[0.25em] transition-colors relative ${
                  tab === t.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
                }`}>
                {t.label}
                {tab === t.k && <span className="absolute left-0 right-0 -bottom-px h-px" style={{ background: "var(--eucalyptus)" }} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Create form */}
      {tab === "create" ? (
        !user || user === false ? (
          <div className="font-fraunces italic text-[var(--cream-soft)]">
            <Link to="/login" className="text-[var(--magenta)]">Logg inn</Link> for å opprette en gruppe.
          </div>
        ) : (
          <form onSubmit={create} className="max-w-xl space-y-6 editorial-card p-8" data-testid="create-group-form">
            <div>
              <div className="font-meta mb-2">Gruppenavn</div>
              <input value={name} onChange={(e)=>setName(e.target.value)} required minLength={2} maxLength={60}
                     className="input-editorial" data-testid="cg-name" />
            </div>
            <div>
              <div className="font-meta mb-2">Beskrivelse <span className="text-[var(--cream-faint)] normal-case tracking-normal text-[10px]">(min 10 tegn, helst 80+)</span></div>
              <textarea value={description} onChange={(e)=>setDescription(e.target.value)} rows={4}
                        className="input-editorial" required data-testid="cg-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <div className="font-meta mb-2">Kategori</div>
                <select value={category} onChange={(e)=>setCategory(e.target.value)} className="input-editorial">
                  {CATS.map(([k, label])=> <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
              <div>
                <div className="font-meta mb-2">Tags <span className="text-[var(--cream-faint)] normal-case tracking-normal text-[10px]">(kommaseparert)</span></div>
                <input value={tags} onChange={(e)=>setTags(e.target.value)}
                       placeholder="oslo, par, helg"
                       className="input-editorial" />
              </div>
            </div>
            <div>
              <div className="font-meta mb-2">Regler (valgfritt)</div>
              <textarea value={rules} onChange={(e)=>setRules(e.target.value)} rows={3}
                        placeholder="Hva er ok / ikke ok i denne gruppen?"
                        className="input-editorial" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope">
              <input type="checkbox" checked={isPrivate} onChange={(e)=>setIsPrivate(e.target.checked)}
                     className="h-4 w-4 accent-[var(--magenta)]" />
              <span>Lukket gruppe (kun medlemmer ser innlegg)</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope" data-testid="requires-approval-toggle">
              <input type="checkbox" checked={requiresApproval} onChange={(e)=>setRequiresApproval(e.target.checked)}
                     className="h-4 w-4 accent-[var(--magenta)]" />
              <span>Krev godkjenning før nye medlemmer slipper inn</span>
            </label>

            <AudienceEditor
              value={audience}
              onChange={setAudience}
              disabled={!isVip}
              reason={!isVip ? "Målgruppe-filter krever VIP-medlemskap. Oppgrader for å låse opp." : ""}
            />

            <p className="font-manrope text-[11px] text-[var(--cream-faint)] uppercase tracking-[0.2em] border-t border-white/10 pt-4">
              I framtiden vil vi kunne sette krav som minimum kontoalder eller VIP-medlem-status.
            </p>
            {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">{error}</div>}
            <button type="submit" disabled={submitting} className="btn-magenta" data-testid="cg-submit">
              {submitting ? t("groups_page.creating") : t("groups_page.create_group_btn")}
            </button>
          </form>
        )
      ) : (
        loading ? (
          <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="font-fraunces italic text-[var(--cream-soft)] py-12 text-center">
            {q.trim()
              ? t("groups_page.no_match", { q })
              : tab === "mine"
                ? t("groups_page.not_member_yet")
                : t("groups_page.empty_be_first")}
          </div>
        ) : (
          <div className="flex flex-col gap-2 sm:grid sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-4" data-testid="groups-grid">
            {filtered.map((g) => (
              <Link key={g.id} to={`/grupper/${g.id}`}
                    className="group panel-grupper overflow-hidden flex flex-row sm:flex-col hover:ring-1 transition-all"
                    style={{ ['--tw-ring-color']: 'var(--eucalyptus)', padding: 0 }}
                    data-testid={`group-card-${g.id}`}>
                <div className="w-20 h-20 shrink-0 sm:w-full sm:h-auto sm:aspect-[4/3] flex items-center justify-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--eucalyptus) 25%, transparent), color-mix(in srgb, var(--magenta) 8%, transparent))" }}>
                  {g.cover_url ? (
                    <img src={g.cover_url} alt="" className="w-full h-full object-cover" />
                  ) : g.avatar_url ? (
                    <img src={g.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="h-8 w-8 sm:h-12 sm:w-12" style={{ color: "color-mix(in srgb, var(--eucalyptus) 70%, transparent)" }} />
                  )}
                  {g.cover_url && g.avatar_url && (
                    <img
                      src={g.avatar_url}
                      alt=""
                      className="absolute bottom-2 left-2 h-10 w-10 sm:h-12 sm:w-12 rounded-sm object-cover border-2 border-[var(--noir)] shadow-lg hidden sm:block"
                    />
                  )}
                </div>
                <div className="p-3 sm:p-5 flex-1 flex flex-col min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                    <h3 className="font-fraunces italic text-base sm:text-xl text-[var(--cream)] leading-tight truncate sm:whitespace-normal">{g.name}</h3>
                    <div className="flex items-center gap-1.5 shrink-0 mt-1">
                      {(g.audience?.enforce_visibility || g.audience?.enforce_participation) && (
                        <Target className="h-3.5 w-3.5 text-[var(--magenta)]" title="Målgruppe-filter aktivt" />
                      )}
                      {g.is_private && <Lock className="h-3.5 w-3.5 text-[var(--cream-soft)]" />}
                    </div>
                  </div>
                  <p className="font-manrope text-xs text-[var(--cream-soft)] line-clamp-2 sm:line-clamp-3 leading-relaxed mb-2 sm:mb-4 flex-1">
                    {g.description}
                  </p>
                  <div className="font-meta flex justify-between items-center text-[10px] sm:text-xs">
                    <span>{g.member_count} {t("groups_page.members_short")}</span>
                    {g.last_post_at && <span className="truncate ml-2">{formatRelative(g.last_post_at)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )
      )}
    </Layout>
  );
}
